import { createAction } from 'redux-actions'
import fetch from 'lib/fetch'
import { validateInboundFilterRules, validateInboundTransformRules } from '@segment/event-rules'
import { stringify } from 'querystring'
import { getRulesLogSettings } from 'selectors'

export const SET_RULE_EDITOR = 'SET_RULE_EDITOR'
export const REQUEST_RULE = 'REQUEST_RULE'
export const REQUEST_RULE_ERROR = 'REQUEST_RULE_ERROR'
export const RECEIVE_RULE = 'RECEIVE_RULE'
export const REQUEST_RULES_BY_PROJECT = 'REQUEST_RULES_BY_PROJECT'
export const REQUEST_RULES_BY_PROJECT_ERROR = 'REQUEST_RULES_BY_PROJECT_ERROR'
export const RECEIVE_RULES_BY_PROJECT = 'RECEIVE_RULES_BY_PROJECT'
export const UPDATE_RULE_VALUE = 'UPDATE_RULE_VALUE'
export const UPDATE_RULE_VALUE_ERROR = 'UPDATE_RULE_VALUE_ERROR'
export const UPDATE_RULE = 'UPDATE_RULE'
export const UPDATE_RULE_ERROR = 'UPDATE_RULE_ERROR'
export const CREATE_RULE = 'CREATE_RULE'
export const CREATE_RULE_ERROR = 'CREATE_RULE_ERROR'
export const REQUEST_RULE_COUNTS = 'REQUEST_RULE_COUNTS'
export const REQUEST_RULE_COUNTS_ERROR = 'REQUEST_RULE_COUNTS_ERROR'
export const RECEIVE_RULE_COUNTS = 'RECEIVE_RULE_COUNTS'
export const OPEN_MODAL = 'OPEN_MODAL'
export const CLOSE_MODAL = 'CLOSE_MODAL'
export const SET_ACTIVE_RULE = 'SET_ACTIVE_RULE'
export const SET_ACTIVE_PROJECT = 'SET_ACTIVE_PROJECT'
export const SET_RULES_LOG_SETTINGS = 'SET_RULES_LOG_SETTINGS'
export const CLOSE_RULES_WARNING_DIALOG = 'CLOSE_RULES_WARNING_DIALOG'

export const setRuleEditor = createAction(SET_RULE_EDITOR)
export const openModal = createAction(OPEN_MODAL)
export const closeModal = createAction(CLOSE_MODAL)
export const setActiveRule = createAction(SET_ACTIVE_RULE)
export const setActiveProject = createAction(SET_ACTIVE_PROJECT)
export const setRulesLogSettings = createAction(SET_RULES_LOG_SETTINGS)
export const closeRulesWarningDialog = createAction(CLOSE_RULES_WARNING_DIALOG)

export function fetchRule (workspaceSlug, projectSlug, ruleId) {
  const endpoint = `
    /api/workspaces/${workspaceSlug}/projects/${projectSlug}/rules/${ruleId}
  `.trim()

  return {
    payload: {
      workspaceSlug,
      projectSlug,
      ruleId
    },
    types: {
      request: REQUEST_RULE,
      failure: REQUEST_RULE_ERROR,
      receive: RECEIVE_RULE
    },
    callAPI: () => fetch(endpoint).then(res => {
      if (!res.ok) {
        const err = new Error('Unable to fetch rule')
        err.status = res.status
        throw err
      }
      return res.json()
    })
  }
}

export function updateRule (workspaceSlug, projectSlug, ruleId, update) {
  let validationRes = null
  if (update.type === 'transform') {
    validationRes = validateInboundTransformRules(update.value)
  } else if (update.type === 'inbound') {
    validationRes = validateInboundFilterRules(update.value)
  } else {
    throw new Error(`Unexpected rule type ${update.type}`)
  }
  const { valid, yamlError, schemaErrors } = validationRes
  if (!valid) {
    const err = new Error('Invalid Rules')
    err.yamlError = yamlError
    err.schemaErrors = schemaErrors
    throw err
  }

  const endpoint = `
    /api/workspaces/${workspaceSlug}/projects/${projectSlug}/rules/${ruleId}
  `.trim()

  return {
    payload: {
      workspaceSlug,
      projectSlug,
      ruleId,
      update
    },
    types: {
      request: UPDATE_RULE,
      failure: UPDATE_RULE_ERROR,
      receive: RECEIVE_RULE
    },
    callAPI: () => fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(update)
    }).then(res => {
      if (!res.ok) {
        const err = new Error('Unable to save rule')
        err.status = res.status
        throw err
      }
      return res.json()
    })
  }
}

export function updateRuleValue (workspaceSlug, projectSlug, ruleId, value) {
  const endpoint = `
    /api/workspaces/${workspaceSlug}/projects/${projectSlug}/rules/${ruleId}
  `.trim()

  return {
    payload: {
      workspaceSlug,
      projectSlug,
      ruleId,
      value
    },
    types: {
      request: UPDATE_RULE_VALUE,
      failure: UPDATE_RULE_VALUE_ERROR,
      receive: RECEIVE_RULE
    },
    callAPI: () => fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify({
        value
      })
    }).then(res => {
      if (!res.ok) {
        const err = new Error('Unable to save rule')
        err.status = res.status
        throw err
      }
      return res.json()
    })
  }
}

export function fetchRulesByProject (workspaceSlug, projectSlug) {
  const endpoint = `
    /api/workspaces/${workspaceSlug}/projects/${projectSlug}/rules
  `.trim()

  return {
    payload: {
      workspaceSlug,
      projectSlug
    },
    types: {
      request: REQUEST_RULES_BY_PROJECT,
      failure: REQUEST_RULES_BY_PROJECT_ERROR,
      receive: RECEIVE_RULES_BY_PROJECT
    },
    callAPI: () => fetch(endpoint).then(res => {
      if (!res.ok) {
        const err = new Error('Unable to fetch rules by project')
        err.status = res.status
        throw err
      }

      if (res.status === 204) {
        return null
      }

      return res.json()
    })
  }
}

export function createRule (workspaceSlug, projectSlug, ruleType) {
  const endpoint = `
    /api/workspaces/${workspaceSlug}/projects/${projectSlug}/rules
  `.trim()

  return {
    payload: {
      workspaceSlug,
      projectSlug,
      ruleType
    },
    types: {
      request: CREATE_RULE,
      failure: CREATE_RULE_ERROR,
      receive: RECEIVE_RULE
    },
    callAPI: () => fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        type: ruleType
      })
    }).then(res => {
      if (!res.ok) {
        const err = new Error('Unable to create rule')
        err.status = res.status
        throw err
      }

      if (res.status === 204) {
        return null
      }

      return res.json()
    })
  }
}

function fetchRuleCountsBySource ({workspaceSlug, sourceSlug, type, interval, time}) {
  const query = stringify({
    start: time,
    precision: interval,
    type: type
  })
  const url = `/api/workspaces/${workspaceSlug}/projects/${sourceSlug}/rule-counts?${query}`

  return {
    payload: {
      workspaceSlug,
      sourceSlug
    },
    types: {
      request: REQUEST_RULE_COUNTS,
      failure: REQUEST_RULE_COUNTS_ERROR,
      receive: RECEIVE_RULE_COUNTS
    },
    callAPI: () => fetch(url).then(res => {
      if (!res.ok) {
        const err = new Error('Unable to fetch rule counts')
        err.status = res.status
        throw err
      }

      return res.json()
    })
  }
}

export const fetchRuleCountsForLog = (workspaceSlug, sourceSlug) => (dispatch, getState) => {
  const state = getState()
  const {type, interval, time} = getRulesLogSettings(state)
  dispatch(fetchRuleCountsBySource({workspaceSlug, sourceSlug, type, interval, time}))
}



// WEBPACK FOOTER //
// ./actions/rules.js